import {
  DRUG_FILTERS,
  DRUG_SORT_KEYS,
  type DrugFilter,
  type DrugSortKey,
} from '@/entities/drug/api/drug.api';
import type { SocialProvider } from '@/entities/auth/api/auth.api';
import { isSocialProvider } from '@/entities/auth/social/socialRedirect';
import { AppLayout } from '@/layouts/AppLayout';
import { AccountsPage } from '@/pages/accounts/AccountsPage';
import { AppOnlyPage } from '@/pages/app-only/AppOnlyPage';
import { SocialCallbackPage } from '@/pages/auth-callback/SocialCallbackPage';
import { DrugsPage } from '@/pages/drugs/DrugsPage';
import { LoginPage } from '@/pages/login/LoginPage';
import { PerformanceImageViewerPage } from '@/pages/performance/PerformanceImageViewerPage';
import { PerformanceMonthPage } from '@/pages/performance/PerformanceMonthPage';
import { PerformancePage } from '@/pages/performance/PerformancePage';
import { DrugDrawerHost } from '@/shared/components/drug-drawer/DrugDrawerHost';
import { NotFound } from '@/shared/components/NotFound';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { hasAccessToken } from '@/stores/authStore';
import {
  createRootRoute,
  createRoute,
  createRouter,
  notFound,
  Outlet,
  redirect,
  type SearchSchemaInput,
} from '@tanstack/react-router';
/**
 * search 파서 — 입력 타입에 SearchSchemaInput을 붙여야 Link · navigate에서
 * search를 생략할 수 있다 (붙이지 않으면 출력 타입이 입력으로 쓰여 필수가 된다).
 */
const str = (v: unknown) => (typeof v === 'string' && v !== '' ? v : undefined);
const num = (v: unknown) => {
  const n = Number(v);
  return v !== undefined && v !== '' && Number.isFinite(n) ? n : undefined;
};

/** 모든 화면 공통 — ?drug=보험코드 가 있으면 의약품 상세 드로어를 연다 (M-15 D) */
const validateRootSearch = (
  s: { drug?: string | number } & SearchSchemaInput,
): { drug?: string } => ({
  drug:
    str(s.drug) ?? (typeof s.drug === 'number' ? String(s.drug) : undefined),
});

/** 로그인 후 돌아갈 내부 경로만 허용한다 (외부 주소 · //host 형태 차단) */
const safeRedirect = (v: unknown) =>
  typeof v === 'string' && v.startsWith('/') && !v.startsWith('//')
    ? v
    : undefined;

const PERFORMANCE_TABS = ['all', 'images', 'pharma'] as const;
type PerformanceTab = (typeof PERFORMANCE_TABS)[number];

const rootRoute = createRootRoute({
  component: () => <Outlet />,
  notFoundComponent: NotFound,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE_PATHS.LOGIN,
  // redirect: 로그인 전에 들어오려던 주소 (딥링크 · A3 직접 접근 복귀용)
  // reason: expired(X7) · social_failed(X1) · signup_required(X4), provider: X1 문구의 제공자
  validateSearch: (
    s: {
      redirect?: string;
      reason?: string;
      provider?: string;
    } & SearchSchemaInput,
  ): { redirect?: string; reason?: string; provider?: SocialProvider } => ({
    redirect: safeRedirect(s.redirect),
    reason: str(s.reason),
    provider: isSocialProvider(s.provider) ? s.provider : undefined,
  }),
  beforeLoad: ({ search }) => {
    if (!hasAccessToken()) return;
    if (search.redirect) throw redirect({ href: search.redirect });
    throw redirect({ to: ROUTE_PATHS.ACCOUNTS });
  },
  component: LoginPage,
});

/** SNS 제공자가 돌려보내는 주소 — 로그인 여부와 상관없이 처리한다 */
const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE_PATHS.AUTH_CALLBACK,
  params: {
    parse: ({ provider }): { provider: SocialProvider } => {
      if (!isSocialProvider(provider)) throw notFound();
      return { provider };
    },
    stringify: ({ provider }) => ({ provider }),
  },
  component: SocialCallbackPage,
});

/** 로그인 이후 화면 — 토큰이 없으면 로그인으로 */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  validateSearch: validateRootSearch,
  beforeLoad: ({ location }) => {
    if (hasAccessToken()) return;
    // 로그인 후 원래 주소(?drug= 딥링크 포함)로 돌아오도록 기억해 둔다
    const target = location.href === '/' ? undefined : location.href;
    throw redirect({ to: ROUTE_PATHS.LOGIN, search: { redirect: target } });
  },
  component: () => (
    <>
      <AppLayout />
      <DrugDrawerHost />
    </>
  ),
});

/** 웹 1차에는 홈이 없다 — 루트는 거래처로 보낸다 */
const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  beforeLoad: ({ search }) => {
    // 루트 딥링크(/?drug=)는 배경 화면을 모르므로 의약품 검색(M-10)을 깔고 드로어를 연다 (M-15 D)
    if (search.drug) {
      throw redirect({ to: ROUTE_PATHS.DRUGS, search: { drug: search.drug } });
    }
    throw redirect({ to: ROUTE_PATHS.ACCOUNTS });
  },
});

const accountsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: ROUTE_PATHS.ACCOUNTS,
  component: AccountsPage,
});

const accountDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: ROUTE_PATHS.ACCOUNT_DETAIL,
  component: AccountsPage,
});

const performanceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: ROUTE_PATHS.PERFORMANCE,
  // month: M-08 선택 월 패널 (그래프에서 고른 달)
  validateSearch: (
    s: { year?: string | number; month?: string | number } & SearchSchemaInput,
  ): { year?: number; month?: number } => ({
    year: num(s.year),
    month: num(s.month),
  }),
  component: PerformancePage,
});

const performanceMonthRoute = createRoute({
  getParentRoute: () => appRoute,
  path: ROUTE_PATHS.PERFORMANCE_MONTH,
  validateSearch: (
    s: {
      clinicId?: string | number;
      tab?: string;
      pharma?: string | number;
    } & SearchSchemaInput,
  ): { clinicId?: number; tab?: PerformanceTab; pharma?: number } => ({
    clinicId: num(s.clinicId),
    tab: PERFORMANCE_TABS.find((t) => t === s.tab),
    // M-17 선택 제약사 (0 = 전체)
    pharma: num(s.pharma),
  }),
  component: PerformanceMonthPage,
});

/** M-18 처방 이미지 상세 — 모달에서 별도 페이지로 바뀌었다 (닫기 · 뒤로가기 = 이전 주소) */
const performanceImageRoute = createRoute({
  getParentRoute: () => appRoute,
  path: `${ROUTE_PATHS.PERFORMANCE_MONTH}/images/$imageCode`,
  validateSearch: (
    s: { clinicId?: string | number } & SearchSchemaInput,
  ): { clinicId?: number } => ({ clinicId: num(s.clinicId) }),
  component: PerformanceImageViewerPage,
});

/**
 * M-10 · M-11 의약품 검색 — 검색어 · 필터 · 정렬 · 페이지를 모두 URL에 둔다.
 * 배열(filters · pharma)은 콤마로 이어 붙인 문자열로 들어온다 (stringifySearch 참고).
 */
const toList = (v: unknown): string[] =>
  Array.isArray(v)
    ? v.map(String)
    : typeof v === 'string' && v !== ''
      ? v.split(',')
      : typeof v === 'number'
        ? [String(v)]
        : [];

const drugsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: ROUTE_PATHS.DRUGS,
  validateSearch: (
    s: {
      q?: string;
      tradable?: string | boolean;
      filters?: string | DrugFilter[];
      pharma?: string | number | number[];
      sort?: string;
      page?: string | number;
    } & SearchSchemaInput,
  ): {
    q?: string;
    tradable?: boolean;
    filters?: DrugFilter[];
    pharma?: number[];
    sort?: DrugSortKey;
    page?: number;
  } => {
    const filters = DRUG_FILTERS.filter((f) => toList(s.filters).includes(f));
    const pharma = toList(s.pharma)
      .map(Number)
      .filter((n) => Number.isInteger(n) && n > 0);
    const page = num(s.page);
    return {
      q: str(s.q),
      tradable:
        s.tradable === true || s.tradable === 'true' || s.tradable === '1'
          ? true
          : undefined,
      filters: filters.length ? filters : undefined,
      pharma: pharma.length ? pharma : undefined,
      sort: DRUG_SORT_KEYS.find((k) => k === s.sort),
      page: page && page > 1 ? Math.floor(page) : undefined,
    };
  },
  component: DrugsPage,
});

const appOnlyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: ROUTE_PATHS.APP_ONLY,
  component: AppOnlyPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  authCallbackRoute,
  appRoute.addChildren([
    indexRoute,
    accountsRoute,
    accountDetailRoute,
    performanceRoute,
    performanceMonthRoute,
    performanceImageRoute,
    drugsRoute,
    appOnlyRoute,
  ]),
]);

/**
 * 쿼리스트링은 JSON이 아니라 평문으로 둔다.
 * 기본 직렬화는 숫자처럼 보이는 문자열에 따옴표를 붙여 ?drug=%22645403420%22 가 되므로,
 * 딥링크 규칙(?drug=645403420, M-15 D)과 맞지 않는다. 배열은 콤마로 이어 붙인다.
 */
const parseSearch = (searchStr: string) =>
  Object.fromEntries(new URLSearchParams(searchStr));

const stringifySearch = (search: Record<string, unknown>) => {
  const params = new URLSearchParams();
  Object.entries(search).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    params.set(key, Array.isArray(value) ? value.join(',') : String(value));
  });
  const str = params.toString();
  return str ? `?${str}` : '';
};

export const router = createRouter({ routeTree, parseSearch, stringifySearch });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
